import type { Visibility } from '../../../shared/types';

export type VisibilityOptionValue = 'public' | 'players' | 'gm' | 'share_group';

type VisibilityMetadata = {
  label: string;
  audiencePreview: string;
};

const VISIBILITY_METADATA: Record<VisibilityOptionValue, VisibilityMetadata> = {
  public: {
    label: 'Public',
    audiencePreview: 'Visible to everyone in this campaign, including viewers, players, and GMs.',
  },
  players: {
    label: 'Players',
    audiencePreview: 'Visible to players, GMs, and owners.',
  },
  gm: {
    label: 'GM Only',
    audiencePreview: 'Visible only to GMs and owners.',
  },
  share_group: {
    label: 'Share Group',
    audiencePreview:
      'Visible to linked share groups, directly shared users, and always visible to GMs/owners.',
  },
};

const isKnownVisibility = (value: string): value is VisibilityOptionValue =>
  Object.prototype.hasOwnProperty.call(VISIBILITY_METADATA, value);

export const normalizeVisibility = (value: Visibility | undefined): VisibilityOptionValue => {
  if (typeof value === 'string' && isKnownVisibility(value)) {
    return value;
  }
  return 'public';
};

export const getVisibilityOptions = (
  canSetGmVisibility: boolean
): Array<{ value: VisibilityOptionValue; label: string }> => {
  const base: VisibilityOptionValue[] = ['public', 'players'];
  const gmOnly: VisibilityOptionValue[] = canSetGmVisibility ? ['gm', 'share_group'] : [];
  return [...base, ...gmOnly].map((value) => ({
    value,
    label: VISIBILITY_METADATA[value].label,
  }));
};

export const getVisibilityLabel = (value: Visibility | undefined): string =>
  VISIBILITY_METADATA[normalizeVisibility(value)].label;

export const getVisibilityAudiencePreview = (value: Visibility | undefined): string =>
  VISIBILITY_METADATA[normalizeVisibility(value)].audiencePreview;

export const getVisibilityOptionDescriptions = (
  canSetGmVisibility: boolean
): Record<string, string> => {
  const options = getVisibilityOptions(canSetGmVisibility);
  return options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = getVisibilityAudiencePreview(option.value);
    return acc;
  }, {});
};
